import { useEffect, useState, useCallback } from "react";
import { supabase } from "@/config/supabase";

interface SessionTimeoutState {
  expiresAt: Date | null;
  showWarning: boolean;
  minutesRemaining: number | null;
  isRefreshing: boolean;
}

/**
 * Hook to monitor Supabase session expiration and show warning before timeout
 * Supabase sessions expire after 1 hour by default
 * Shows warning 5 minutes before expiry to prevent lost work
 */
export function useSessionTimeout() {
  const [state, setState] = useState<SessionTimeoutState>({
    expiresAt: null,
    showWarning: false,
    minutesRemaining: null,
    isRefreshing: false,
  });

  const WARNING_MINUTES = 5; // Show warning 5 minutes before expiry

  const checkSession = useCallback(async () => {
    const {
      data: { session },
      error,
    } = await supabase.auth.getSession();

    if (error) {
      console.error("Error checking session:", error);
      return;
    }

    if (session?.expires_at) {
      const expiryTime = new Date(session.expires_at * 1000);
      const now = Date.now();
      const timeUntilExpiry = expiryTime.getTime() - now;
      const minutesRemaining = Math.floor(timeUntilExpiry / (60 * 1000));

      // Show warning if less than WARNING_MINUTES remaining
      const shouldShowWarning = minutesRemaining <= WARNING_MINUTES && minutesRemaining > 0;

      setState((prev) => ({
        ...prev,
        expiresAt: expiryTime,
        showWarning: shouldShowWarning,
        minutesRemaining,
      }));
    } else {
      // No session or no expiry time
      setState((prev) => ({
        ...prev,
        expiresAt: null,
        showWarning: false,
        minutesRemaining: null,
      }));
    }
  }, [WARNING_MINUTES]);

  /**
   * Refresh the session to extend it for another hour
   */
  const refreshSession = useCallback(async () => {
    setState((prev) => ({ ...prev, isRefreshing: true }));

    try {
      const { error } = await supabase.auth.refreshSession();

      if (error) {
        console.error("Error refreshing session:", error);
        throw error;
      }

      // Re-check session after refresh
      await checkSession();

      setState((prev) => ({
        ...prev,
        showWarning: false,
        isRefreshing: false,
      }));

      return { success: true };
    } catch (error) {
      setState((prev) => ({ ...prev, isRefreshing: false }));
      return { success: false, error };
    }
  }, [checkSession]);

  /**
   * Dismiss the warning (useful if user wants to continue without refreshing)
   */
  const dismissWarning = useCallback(() => {
    setState((prev) => ({ ...prev, showWarning: false }));
  }, []);

  useEffect(() => {
    // Initial check
    checkSession();

    // Check every minute
    const interval = setInterval(checkSession, 60 * 1000);

    return () => clearInterval(interval);
  }, [checkSession]);

  return {
    expiresAt: state.expiresAt,
    showWarning: state.showWarning,
    minutesRemaining: state.minutesRemaining,
    isRefreshing: state.isRefreshing,
    refreshSession,
    dismissWarning,
  };
}
